import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { DateInput } from "../components/DateInput";
import { ModalCloseButton } from "../components/ModalCloseButton";
import { QuickEditSheet, type QuickEditValue } from "../components/QuickEditSheet";
import { api, type ApiError } from "../lib/api";
import { formatExpirationMonth } from "../lib/expiration";
import { sortRowsForRelatedDisplay } from "../lib/relatedRowOrdering";
import { buildRelatedRowMeta, relatedRowClass } from "../lib/relatedRows";
import { removeReasonAuditNote, TRUCK_CHECK_REMOVE_REASON_OPTIONS } from "../lib/removeReasons";
import { openedTruckCheckNotice } from "../lib/truckCheckNotices";
import { truckCheckVerificationPrompt } from "../lib/truckCheckPrompts";
import { canModifyTruckCheckTasks } from "../lib/truckCheckPermissions";
import {
  selectTruckCheckBagId,
  type TruckCheckViewPatch,
  type TruckCheckViewState
} from "../lib/truckCheckViewState";
import { applyDailyTruckCheckTaskVerification } from "../lib/truckCheckPatches";
import { createLatestRequestGate, type LatestRequestToken } from "../lib/latestRequest";
import type {
  Apparatus,
  DailyTruckCheckDetails,
  DailyTruckCheckHistoryRow,
  DailyTruckCheckTask,
  DailyTruckCheckTaskVerificationResult,
  StockLotChoice
} from "../lib/types";
import { useRegisterViewRefresh, useUnsavedChangesWarning } from "../lib/viewRefresh";

function isoToday() {
  return new Date().toISOString().slice(0, 10);
}

function fmt(value: string | null | undefined) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return value;
  return parsed.toLocaleString();
}

function dailyDisplayQuantity(task: DailyTruckCheckTask): number {
  return task.verified && task.observedQuantity != null
    ? task.observedQuantity
    : task.startingQuantity ?? 0;
}

function dailyDisplayExpiration(task: DailyTruckCheckTask): string | null {
  return task.verified && task.observedExpirationDate !== undefined
    ? task.observedExpirationDate
    : task.startingExpirationDate ?? null;
}

function initialValueForTask(task: DailyTruckCheckTask, enteredName: string): QuickEditValue {
  const quantity = task.observedQuantity ?? task.startingQuantity ?? 0;
  return {
    enteredName,
    quantity,
    expirationDate: quantity <= 0 ? "" : task.observedExpirationDate ?? task.startingExpirationDate ?? "",
    notes: task.observedNotes ?? task.startingNotes ?? task.note ?? "",
    sourceType: "",
    stockLotId: "",
    stockTransferQuantity: quantity > 0 ? quantity : 1
  };
}

function dailySource(value: QuickEditValue): "stock" | "other" | null {
  return value.sourceType === "stock" || value.sourceType === "other" ? value.sourceType : null;
}

function statusLabel(status: DailyTruckCheckDetails["check"]["status"] | DailyTruckCheckHistoryRow["status"]): string {
  if (status === "in_progress") return "In Progress";
  if (status === "completed") return "Completed";
  if (status === "abandoned") return "Abandoned";
  return status;
}

function statusClass(status: DailyTruckCheckDetails["check"]["status"] | DailyTruckCheckHistoryRow["status"]): string {
  if (status === "completed") return "truck-check-status-completed";
  if (status === "abandoned") return "truck-check-status-abandoned";
  return "truck-check-status-progress";
}

function readOnlyMessage(status: DailyTruckCheckDetails["check"]["status"]): string {
  if (status === "completed") return "Completed report. Read-only.";
  if (status === "abandoned") return "Abandoned check. Read-only unless resumed.";
  return "";
}

function taskStatusChip(task: DailyTruckCheckTask) {
  if (!task.verified) return null;
  if (task.resultStatus === "deficient") return <span className="reason-chip reason-chip-red">Deficient</span>;
  if (task.resultStatus === "corrected") return <span className="reason-chip reason-chip-ok">Corrected</span>;
  return <span className="reason-chip reason-chip-ok">Verified</span>;
}

function isDailyTaskMissing(task: DailyTruckCheckTask, tasks: DailyTruckCheckTask[]): boolean {
  if (task.taskType !== "item" || !task.catalogItemId) return false;
  const total = tasks
    .filter((candidate) => candidate.taskType === "item" && candidate.catalogItemId === task.catalogItemId)
    .reduce((sum, candidate) => sum + dailyDisplayQuantity(candidate), 0);
  return total <= 0;
}

export function dailyBagDisplayName(bagName: string): string {
  const normalized = bagName.trim().toLowerCase();
  if (normalized === "aed") return "AED Kit";
  if (normalized === "other") return "All Other Kits";
  return bagName;
}

function isAllOtherKitsGroup(bagName: string, sectionName: string): boolean {
  return dailyBagDisplayName(bagName) === "All Other Kits" && sectionName === "Kit Presence";
}

export function shouldShowDailyGroupHeading(bagName: string, sectionName: string): boolean {
  return !isAllOtherKitsGroup(bagName, sectionName) && sectionName !== dailyBagDisplayName(bagName);
}

export function dailyTaskDisplayName(task: DailyTruckCheckTask): string {
  if (task.sectionName === "Kit Presence" && task.itemName === "Kit Presence") return "Kits are Present";
  return task.itemName;
}

export function shouldShowDailyVisualSection(bagName: string, task: DailyTruckCheckTask): boolean {
  if (task.taskType !== "visual" || task.itemName === "Visual confirmation") return false;
  if (!shouldShowDailyGroupHeading(bagName, task.sectionName)) return false;
  return !["Main Compartment", "Top Outer Compartment - Assessment"].includes(task.sectionName);
}

type DailyBagDetails = DailyTruckCheckDetails["bags"][number];

export function sortDailyBagsForDisplay(bags: DailyBagDetails[]): DailyBagDetails[] {
  return [...bags].sort((a, b) => {
    const aOther = dailyBagDisplayName(a.bagName) === "All Other Kits";
    const bOther = dailyBagDisplayName(b.bagName) === "All Other Kits";
    return aOther === bOther ? 0 : aOther ? 1 : -1;
  });
}

type DailyVisualHelpDetails = {
  label: string;
  items: string[];
  reportProblemText: string | null;
};

export function dailyVisualHelpText(task: DailyTruckCheckTask): string {
  const configuredHelp = task.startingNotes?.trim();
  if (configuredHelp) return configuredHelp;
  if (task.itemName === "No missing airway adjuncts visible") {
    return "Report Problem if any airway adjunct is missing or visibly deficient.";
  }
  if (task.itemName === "Canister & tubing attached") {
    return "Report Problem if the canister or tubing is missing, unattached, or visibly deficient.";
  }
  if (task.itemName === "Catheters present") {
    return "Report Problem if suction catheters are missing or visibly deficient.";
  }
  if (task.sectionName === "PPE Kit" && task.itemName === "Present") {
    return "Report Problem if PPE supplies appear missing, low, or visibly deficient.";
  }
  if (task.itemName.endsWith(" present")) {
    return "Report Problem if this kit is not present or is visibly deficient.";
  }
  return "Visual confirmation of this pouch/compartment only";
}

export function dailyVisualHelpDetails(task: DailyTruckCheckTask): DailyVisualHelpDetails | null {
  const text = dailyVisualHelpText(task).trim();
  const lines = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  if (lines[0]?.toLowerCase() === "verify:") {
    const items: string[] = [];
    const reportLines: string[] = [];
    for (const line of lines.slice(1)) {
      if (line.startsWith("- ")) {
        items.push(line.slice(2).trim());
      } else {
        reportLines.push(line);
      }
    }
    return items.length > 0 ? { label: "Verify:", items, reportProblemText: reportLines.join(" ") || null } : null;
  }

  const legacyMatch = text.match(/^Verify:\s*(.+?)(?:\.\s+(Report Problem .+))?$/);
  if (!legacyMatch) return null;

  const items = legacyMatch[1]
    .split(";")
    .map((item) => item.trim().replace(/\.$/, ""))
    .filter(Boolean);
  return items.length > 0 ? { label: "Verify:", items, reportProblemText: legacyMatch[2] ?? null } : null;
}

function DailyVisualHelp({ task }: { task: DailyTruckCheckTask }) {
  const details = dailyVisualHelpDetails(task);
  if (!details) return <p className="muted small">{dailyVisualHelpText(task)}</p>;

  return (
    <div className="daily-visual-help muted small">
      <span className="reason-label">{details.label}</span>
      <ul>
        {details.items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
      {details.reportProblemText ? <p>{details.reportProblemText}</p> : null}
    </div>
  );
}

export function dailyProblemNote(task: DailyTruckCheckTask): string | null {
  if (task.taskType !== "visual" || task.visualStatus !== "problem") return null;
  const note = task.note?.trim();
  return note ? note : null;
}

export function dailyVerificationByline(task: DailyTruckCheckTask): { label: string; className: string } {
  if (task.taskType === "visual" && task.visualStatus === "problem") {
    return { label: "Problem reported by", className: "error-text" };
  }
  return { label: "Verified by", className: "success-text" };
}

export function initialDailyVisualStatus(task: DailyTruckCheckTask): "verified" | "problem" {
  return task.taskType === "visual" && task.visualStatus === "problem" ? "problem" : "verified";
}

export function DailyTruckCheckPanel({
  enteredName,
  rememberName,
  viewState,
  updateViewState
}: {
  enteredName: string;
  rememberName: (name: string) => void;
  viewState: TruckCheckViewState;
  updateViewState: (patch: TruckCheckViewPatch) => void;
}) {
  const [apparatus, setApparatus] = useState<Apparatus[]>([]);
  const [activeCheck, setActiveCheck] = useState<DailyTruckCheckDetails | null>(null);
  const [history, setHistory] = useState<DailyTruckCheckHistoryRow[]>([]);
  const [historyStatus, setHistoryStatus] = useState<"all" | "in_progress" | "completed" | "abandoned">("all");
  const [loadedApparatusId, setLoadedApparatusId] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState("");
  const [openNotice, setOpenNotice] = useState("");

  const [startName, setStartName] = useState(enteredName);
  const [startNote, setStartNote] = useState("");

  const [taskModal, setTaskModal] = useState<DailyTruckCheckTask | null>(null);
  const [visualName, setVisualName] = useState(enteredName);
  const [visualStatus, setVisualStatus] = useState<"verified" | "problem">("verified");
  const [visualNote, setVisualNote] = useState("");
  const [stockLots, setStockLots] = useState<StockLotChoice[]>([]);
  const [stockLotsLoading, setStockLotsLoading] = useState(false);

  const [completeOpen, setCompleteOpen] = useState(false);
  const [completeName, setCompleteName] = useState(enteredName);
  const [completeDate, setCompleteDate] = useState(isoToday());
  const [completeNote, setCompleteNote] = useState("");
  const [abandonOpen, setAbandonOpen] = useState(false);
  const [abandonName, setAbandonName] = useState(enteredName);
  const [abandonNote, setAbandonNote] = useState("");
  const [resumeCheck, setResumeCheck] = useState<DailyTruckCheckHistoryRow | null>(null);
  const [resumeName, setResumeName] = useState(enteredName);
  const [resumeNote, setResumeNote] = useState("");
  const [deleteCheck, setDeleteCheck] = useState<DailyTruckCheckHistoryRow | null>(null);
  const apparatusRequestsRef = useRef(createLatestRequestGate());
  const scopeRequestsRef = useRef(createLatestRequestGate());
  const historyRequestsRef = useRef(createLatestRequestGate());
  const stockLotRequestsRef = useRef(createLatestRequestGate());
  const activeCheckRef = useRef<DailyTruckCheckDetails | null>(null);
  const selectedApparatusIdRef = useRef(viewState.apparatusId);
  const selectedApparatusId = viewState.apparatusId;
  const selectedBagId = viewState.bagId;
  const viewingHistoryCheckId = viewState.historyView ? viewState.checkId : "";
  selectedApparatusIdRef.current = selectedApparatusId;

  const isCurrentApparatusRequest = (request: LatestRequestToken, apparatusId: string) =>
    request.isCurrent() && selectedApparatusIdRef.current === apparatusId;

  const commitActiveCheck = (nextCheck: DailyTruckCheckDetails | null) => {
    activeCheckRef.current = nextCheck;
    setActiveCheck(nextCheck);
  };

  const reconcileActiveHistoryRow = (rows: DailyTruckCheckHistoryRow[]) => {
    const currentCheck = activeCheckRef.current;
    if (!currentCheck) return rows;
    const tasks = currentCheck.bags.flatMap((bag) => bag.tasks);
    return rows.map((row) =>
      row.id === currentCheck.check.id
        ? {
            ...row,
            totalTasks: currentCheck.progress.totalTasks,
            verifiedTasks: currentCheck.progress.verifiedTasks,
            deficientTasks: tasks.filter((task) => task.verified && task.resultStatus === "deficient").length,
            correctedTasks: tasks.filter((task) => task.verified && task.resultStatus === "corrected").length
          }
        : row
    );
  };

  const loadApparatus = async (request: LatestRequestToken) => {
    const result = await api.getApparatus({
      signal: request.signal,
      dedupe: false
    });
    if (!request.isCurrent()) return;

    setApparatus(result.apparatus);
    const currentApparatusId = selectedApparatusIdRef.current;
    const requestedApparatusExists = result.apparatus.some((row) => row.id === currentApparatusId);
    if (!requestedApparatusExists && result.apparatus.length > 0 && request.isCurrent()) {
      updateViewState({
        apparatusId: result.apparatus[0].id,
        checkId: viewState.checkId || null,
        bagId: viewState.bagId || null,
        historyView: viewState.historyView
      });
    }
  };

  const refreshForApparatus = async (apparatusId: string, request: LatestRequestToken) => {
    if (!apparatusId) return;
    let effectiveApparatusId = apparatusId;
    let nextCheck: DailyTruckCheckDetails | null = null;
    let restoredHistoryView = false;

    if (viewState.checkId) {
      try {
        nextCheck = await api.getDailyTruckCheck(viewState.checkId, {
          signal: request.signal,
          dedupe: false
        });
        if (!isCurrentApparatusRequest(request, apparatusId)) return;
        effectiveApparatusId = nextCheck.check.apparatusId;
        restoredHistoryView = viewState.historyView;
      } catch (err: any) {
        if (!isCurrentApparatusRequest(request, apparatusId)) return;
        if (err?.code !== "DAILY_TRUCK_CHECK_NOT_FOUND") throw err;
        setOpenNotice("That saved daily check is no longer available. Showing the current check instead.");
        const active = await api.getActiveDailyTruckCheck(apparatusId, {
          signal: request.signal,
          dedupe: false
        });
        if (!isCurrentApparatusRequest(request, apparatusId)) return;
        nextCheck = active.check;
      }
    } else {
      const active = await api.getActiveDailyTruckCheck(apparatusId, {
        signal: request.signal,
        dedupe: false
      });
      if (!isCurrentApparatusRequest(request, apparatusId)) return;
      nextCheck = active.check;
    }

    if (!isCurrentApparatusRequest(request, apparatusId)) return;
    commitActiveCheck(nextCheck);
    setLoadedApparatusId(effectiveApparatusId);
    if (nextCheck) {
      const nextBagId = selectTruckCheckBagId(nextCheck.bags, selectedBagId);
      updateViewState({
        apparatusId: effectiveApparatusId,
        checkId: nextCheck.check.id,
        bagId: nextBagId || null,
        historyView: restoredHistoryView
      });
      if (restoredHistoryView) {
        setOpenNotice(openedTruckCheckNotice("daily", nextCheck.check.apparatusName, statusLabel(nextCheck.check.status)));
      }
    } else {
      updateViewState({
        apparatusId: effectiveApparatusId,
        checkId: null,
        bagId: null,
        historyView: false
      });
    }

    return effectiveApparatusId;
  };

  const refreshHistoryForApparatus = async (apparatusId: string) => {
    const request = historyRequestsRef.current.begin();
    setHistoryLoading(true);
    try {
      const hist = await api.getDailyTruckCheckHistory(apparatusId, historyStatus, 50, {
        signal: request.signal,
        dedupe: false
      });
      if (!isCurrentApparatusRequest(request, apparatusId)) return;
      setHistory(reconcileActiveHistoryRow(hist.rows));
    } catch (err) {
      if (!isCurrentApparatusRequest(request, apparatusId)) return;
      throw err;
    } finally {
      if (request.isCurrent()) setHistoryLoading(false);
    }
  };

  const refreshHistory = async () => {
    if (!selectedApparatusId || loadedApparatusId !== selectedApparatusId) return;
    await refreshHistoryForApparatus(selectedApparatusId);
  };

  const refreshHistoryAfterSave = async (successMessage: string) => {
    try {
      await refreshHistory();
    } catch {
      setOpenNotice(`${successMessage} History could not be refreshed; use Refresh when the connection improves.`);
    }
  };

  const afterTaskUpdate = async (updated: DailyTruckCheckDetails) => {
    commitActiveCheck(updated);
    setLoadedApparatusId(updated.check.apparatusId);
    const nextBagId = selectTruckCheckBagId(updated.bags, selectedBagId);
    updateViewState({
      apparatusId: updated.check.apparatusId,
      checkId: updated.check.id,
      bagId: nextBagId || null,
      historyView: viewState.historyView
    });
    await refreshHistoryAfterSave("Daily truck check saved.");
  };

  const afterTaskVerification = (result: DailyTruckCheckTaskVerificationResult) => {
    if (!activeCheck) return;
    const updated = applyDailyTruckCheckTaskVerification(activeCheck, result);
    commitActiveCheck(updated);
    setLoadedApparatusId(updated.check.apparatusId);
    const nextBagId = selectTruckCheckBagId(updated.bags, selectedBagId);
    updateViewState({
      apparatusId: updated.check.apparatusId,
      checkId: updated.check.id,
      bagId: nextBagId || null,
      historyView: viewState.historyView
    });

    const tasks = updated.bags.flatMap((bag) => bag.tasks);
    setHistory((current) =>
      current.map((row) =>
        row.id === updated.check.id
          ? {
              ...row,
              totalTasks: updated.progress.totalTasks,
              verifiedTasks: updated.progress.verifiedTasks,
              deficientTasks: tasks.filter((task) => task.verified && task.resultStatus === "deficient").length,
              correctedTasks: tasks.filter((task) => task.verified && task.resultStatus === "corrected").length
            }
          : row
      )
    );
  };

  const closeTaskModal = () => {
    stockLotRequestsRef.current.cancel();
    setStockLots([]);
    setStockLotsLoading(false);
    setTaskModal(null);
  };

  useEffect(() => {
    const request = apparatusRequestsRef.current.begin();
    setLoading(true);
    loadApparatus(request)
      .catch((err: ApiError) => {
        if (request.isCurrent()) setError(err.message);
      })
      .finally(() => {
        if (request.isCurrent()) setLoading(false);
      });
    return () => apparatusRequestsRef.current.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedApparatusId) {
      scopeRequestsRef.current.cancel();
      historyRequestsRef.current.cancel();
      stockLotRequestsRef.current.cancel();
      setLoadedApparatusId("");
      commitActiveCheck(null);
      setHistory([]);
      setBusy(false);
      setHistoryLoading(false);
      return;
    }
    const request = scopeRequestsRef.current.begin();
    historyRequestsRef.current.cancel();
    stockLotRequestsRef.current.cancel();
    setOpenNotice("");
    setTaskModal(null);
    setStockLots([]);
    setStockLotsLoading(false);
    setCompleteOpen(false);
    setAbandonOpen(false);
    setResumeCheck(null);
    setDeleteCheck(null);
    setLoadedApparatusId("");
    commitActiveCheck(null);
    setHistory([]);
    setBusy(true);
    setHistoryLoading(false);
    setError("");
    refreshForApparatus(selectedApparatusId, request)
      .then((effectiveApparatusId) => {
        if (!isCurrentApparatusRequest(request, selectedApparatusId)) return;
        setBusy(false);
        if (effectiveApparatusId === selectedApparatusIdRef.current) {
          void refreshHistoryForApparatus(effectiveApparatusId).catch((err: ApiError) => {
            if (selectedApparatusIdRef.current === effectiveApparatusId) setError(err.message);
          });
        }
      })
      .catch((err: ApiError) => {
        if (isCurrentApparatusRequest(request, selectedApparatusId)) setError(err.message);
      })
      .finally(() => {
        if (isCurrentApparatusRequest(request, selectedApparatusId)) setBusy(false);
      });
    return () => scopeRequestsRef.current.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedApparatusId]);

  useEffect(() => {
    if (!selectedApparatusId || loadedApparatusId !== selectedApparatusId) return;
    refreshHistory().catch((err: ApiError) => setError(err.message));
    return () => historyRequestsRef.current.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyStatus]);

  useEffect(
    () => () => {
      historyRequestsRef.current.cancel();
      stockLotRequestsRef.current.cancel();
    },
    []
  );

  useEffect(() => {
    setStartName(enteredName);
    setVisualName(enteredName);
    setCompleteName(enteredName);
    setAbandonName(enteredName);
    setResumeName(enteredName);
  }, [enteredName]);

  useEffect(() => {
    if (!openNotice) return;
    const timeoutId = window.setTimeout(() => setOpenNotice(""), 5000);
    return () => window.clearTimeout(timeoutId);
  }, [openNotice]);

  const editorOpen = Boolean(taskModal || completeOpen || abandonOpen || resumeCheck || deleteCheck);
  useUnsavedChangesWarning(editorOpen);
  useRegisterViewRefresh(
    async () => {
      if (!selectedApparatusId) return;
      const request = scopeRequestsRef.current.begin();
      historyRequestsRef.current.cancel();
      setHistoryLoading(false);
      setBusy(true);
      setError("");
      try {
        const effectiveApparatusId = await refreshForApparatus(selectedApparatusId, request);
        if (!isCurrentApparatusRequest(request, selectedApparatusId)) return;
        setBusy(false);
        if (effectiveApparatusId === selectedApparatusIdRef.current) {
          await refreshHistoryForApparatus(effectiveApparatusId);
        }
      } catch (err: any) {
        if (!isCurrentApparatusRequest(request, selectedApparatusId)) return;
        setError(err?.message ?? "Failed to refresh daily truck check");
        throw err;
      } finally {
        if (isCurrentApparatusRequest(request, selectedApparatusId)) setBusy(false);
      }
    },
    {
      disabled: loading || busy || editorOpen,
      disabledReason: editorOpen ? "Finish or close the open editor before refreshing." : "Refresh is already in progress."
    }
  );

  const selectedBag = useMemo(
    () => activeCheck?.bags.find((bag) => bag.bagId === selectedBagId) ?? activeCheck?.bags[0] ?? null,
    [activeCheck, selectedBagId]
  );

  const dailyBagsForDisplay = useMemo(
    () => sortDailyBagsForDisplay(activeCheck?.bags ?? []),
    [activeCheck?.bags]
  );

  const selectedBagTaskGroups = useMemo(() => {
    const groups = new Map<string, DailyTruckCheckTask[]>();
    for (const task of selectedBag?.tasks ?? []) {
      const group = groups.get(task.sectionName) ?? [];
      group.push(task);
      groups.set(task.sectionName, group);
    }
    return [...groups.entries()].map(([name, items]) => ({ name, items: sortRowsForRelatedDisplay(items) }));
  }, [selectedBag]);

  const selectedBagVisibleTasks = useMemo(
    () => selectedBagTaskGroups.flatMap((group) => group.items),
    [selectedBagTaskGroups]
  );

  const selectedBagRelatedMetaByKey = useMemo(
    () =>
      buildRelatedRowMeta(
        selectedBagVisibleTasks.map((task) => ({
          rowKey: task.taskId,
          catalogItemId: task.catalogItemId
        }))
      ),
    [selectedBagVisibleTasks]
  );

  const openTaskModal = async (task: DailyTruckCheckTask) => {
    if (!canModifyTruckCheckTasks(activeCheck?.check.status)) return;
    const request = stockLotRequestsRef.current.begin();
    setTaskModal(task);
    setVisualName(enteredName);
    setVisualStatus(initialDailyVisualStatus(task));
    setVisualNote(task.note ?? task.observedNotes ?? "");
    setStockLots([]);

    if (task.taskType === "item" && task.catalogItemId) {
      setStockLotsLoading(true);
      try {
        const result = await api.getStockLotsForCatalogItem(task.catalogItemId, {
          signal: request.signal,
          dedupe: false
        });
        if (!request.isCurrent()) return;
        setStockLots(result.items);
      } catch {
        if (!request.isCurrent()) return;
        setStockLots([]);
      } finally {
        if (request.isCurrent()) setStockLotsLoading(false);
      }
    } else if (request.isCurrent()) {
      setStockLotsLoading(false);
    }
  };

  const verifyItemTask = async (
    value: QuickEditValue,
    resolutionAction: "verify" | "add_expiration" | "delete" = "verify"
  ) => {
    if (!activeCheck || !taskModal) return;
    if (!canModifyTruckCheckTasks(activeCheck.check.status)) {
      setError("Completed daily truck checks are read-only.");
      return;
    }
    const name = value.enteredName.trim();
    if (!name) return;

    setBusy(true);
    setError("");
    try {
      rememberName(name);
      const updated = await api.verifyDailyTruckCheckTask(activeCheck.check.id, {
        taskId: taskModal.taskId,
        enteredName: name,
        observedQuantity: Math.max(0, Math.round(value.quantity)),
        observedExpirationDate: value.expirationDate || null,
        observedNotes: value.notes || null,
        note: resolutionAction === "delete" ? removeReasonAuditNote(value.removeReason) : value.notes || null,
        sourceType: dailySource(value),
        stockLotId: value.stockLotId || null,
        stockTransferQuantity: value.stockTransferQuantity ?? null,
        resolutionAction,
        applyToInventory: true,
        checkRevision: activeCheck.check.updatedAt
      });
      closeTaskModal();
      afterTaskVerification(updated);
    } catch (err: any) {
      setError(err?.message ?? "Failed to verify daily task");
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const submitVisualTask = async (event: FormEvent) => {
    event.preventDefault();
    if (!activeCheck || !taskModal) return;
    if (!canModifyTruckCheckTasks(activeCheck.check.status)) {
      setError("Completed daily truck checks are read-only.");
      return;
    }
    const name = visualName.trim();
    const note = visualNote.trim();
    if (!name) return;
    if (visualStatus === "problem" && !note) {
      setError("A note is required when reporting a problem.");
      return;
    }

    setBusy(true);
    setError("");
    try {
      rememberName(name);
      const updated = await api.verifyDailyTruckCheckTask(activeCheck.check.id, {
        taskId: taskModal.taskId,
        enteredName: name,
        visualStatus,
        note: note || null,
        resolutionAction: "verify",
        checkRevision: activeCheck.check.updatedAt
      });
      closeTaskModal();
      afterTaskVerification(updated);
    } catch (err: any) {
      setError(err?.message ?? "Failed to verify visual check");
    } finally {
      setBusy(false);
    }
  };

  const startCheck = async () => {
    const name = startName.trim();
    if (!selectedApparatusId || !name) return;
    setBusy(true);
    setError("");
    try {
      rememberName(name);
      const check = await api.startDailyTruckCheck({
        apparatusId: selectedApparatusId,
        enteredName: name,
        note: startNote || null
      });
      setOpenNotice("");
      commitActiveCheck(check);
      setLoadedApparatusId(check.check.apparatusId);
      setStartNote("");
      updateViewState({
        apparatusId: check.check.apparatusId,
        checkId: check.check.id,
        bagId: selectTruckCheckBagId(check.bags, "") || null,
        historyView: false
      });
      await refreshHistoryAfterSave("Daily truck check opened.");
    } catch (err: any) {
      setError(err?.message ?? "Failed to start daily truck check");
    } finally {
      setBusy(false);
    }
  };

  const completeCheck = async (event: FormEvent) => {
    event.preventDefault();
    if (!activeCheck) return;
    const name = completeName.trim();
    if (!name) return;
    if (!completeDate) {
      setError("Sign-off date is required.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      rememberName(name);
      const completed = await api.completeDailyTruckCheck(activeCheck.check.id, {
        enteredName: name,
        signoffDate: completeDate || null,
        note: completeNote || null
      });
      setOpenNotice("");
      setCompleteOpen(false);
      await afterTaskUpdate(completed);
    } catch (err: any) {
      setError(err?.message ?? "Failed to complete daily check");
    } finally {
      setBusy(false);
    }
  };

  const abandonCheck = async (event: FormEvent) => {
    event.preventDefault();
    if (!activeCheck) return;
    const name = abandonName.trim();
    if (!name) return;
    setBusy(true);
    setError("");
    try {
      rememberName(name);
      const abandoned = await api.abandonDailyTruckCheck(activeCheck.check.id, {
        enteredName: name,
        note: abandonNote || null
      });
      commitActiveCheck(null);
      setOpenNotice("");
      updateViewState({
        checkId: null,
        bagId: null,
        historyView: false
      });
      setAbandonOpen(false);
      setAbandonNote("");
      await refreshHistoryAfterSave("Daily truck check was moved to history.");
      if (abandoned.check.status !== "abandoned") {
        commitActiveCheck(abandoned);
        updateViewState({
          apparatusId: abandoned.check.apparatusId,
          checkId: abandoned.check.id,
          bagId: selectTruckCheckBagId(abandoned.bags, "") || null,
          historyView: false
        });
      }
    } catch (err: any) {
      setError(err?.message ?? "Failed to quit daily check");
    } finally {
      setBusy(false);
    }
  };

  const resumeHistoryCheck = async (id: string) => {
    const request = scopeRequestsRef.current.begin();
    historyRequestsRef.current.cancel();
    setBusy(true);
    setError("");
    try {
      const details = await api.getDailyTruckCheck(id, {
        signal: request.signal,
        dedupe: false
      });
      if (!isCurrentApparatusRequest(request, selectedApparatusId)) return;
      commitActiveCheck(details);
      setLoadedApparatusId(details.check.apparatusId);
      updateViewState({
        apparatusId: details.check.apparatusId,
        checkId: details.check.id,
        bagId: selectTruckCheckBagId(details.bags, selectedBagId) || null,
        historyView: true
      });
      setOpenNotice(
        openedTruckCheckNotice("daily", details.check.apparatusName, statusLabel(details.check.status))
      );
    } catch (err: any) {
      if (!isCurrentApparatusRequest(request, selectedApparatusId)) return;
      setError(err?.message ?? "Failed to open daily check");
    } finally {
      if (isCurrentApparatusRequest(request, selectedApparatusId)) setBusy(false);
    }
  };

  const reopenAbandonedCheck = async (event: FormEvent) => {
    event.preventDefault();
    if (!resumeCheck) return;
    const name = resumeName.trim();
    if (!name) return;
    setBusy(true);
    setError("");
    try {
      rememberName(name);
      const details = await api.reopenDailyTruckCheck(resumeCheck.id, {
        enteredName: name,
        note: resumeNote || null
      });
      setOpenNotice("");
      commitActiveCheck(details);
      updateViewState({
        apparatusId: details.check.apparatusId,
        checkId: details.check.id,
        bagId: selectTruckCheckBagId(details.bags, selectedBagId) || null,
        historyView: false
      });
      setResumeCheck(null);
      setResumeNote("");
      await refreshHistoryAfterSave("Daily truck check resumed.");
    } catch (err: any) {
      setError(err?.message ?? "Failed to resume daily check");
    } finally {
      setBusy(false);
    }
  };

  const deleteAbandonedCheck = async () => {
    if (!deleteCheck) return;
    setBusy(true);
    setError("");
    try {
      await api.deleteAbandonedDailyTruckCheck(deleteCheck.id);
      if (activeCheck?.check.id === deleteCheck.id) {
        commitActiveCheck(null);
        setOpenNotice("");
        updateViewState({
          checkId: null,
          bagId: null,
          historyView: false
        });
      }
      setDeleteCheck(null);
      await refreshHistoryAfterSave("Abandoned daily truck check deleted.");
    } catch (err: any) {
      setError(err?.message ?? "Failed to delete abandoned daily check");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p className="muted">Loading daily truck checks...</p>;

  const scopeReady = Boolean(selectedApparatusId) && loadedApparatusId === selectedApparatusId;
  const itemTaskModal = taskModal?.taskType === "item" ? taskModal : null;
  const visualTaskModal = taskModal?.taskType === "visual" ? taskModal : null;
  const canModifyActiveCheck = scopeReady && canModifyTruckCheckTasks(activeCheck?.check.status);
  const activeReadOnlyMessage = activeCheck ? readOnlyMessage(activeCheck.check.status) : "";
  const historyBusy = busy || historyLoading;

  return (
    <div className="stack-md">
      {error ? <p className="error-text">{error}</p> : null}

      <section className="card stack-sm">
        <h2>Daily Truck Check</h2>
        <p className="muted small">Quick QRS Jump Kit check. Other kits will be added step-by-step.</p>
        <label className="stack-xs">
          <span className="label">Apparatus</span>
          <select
            className="input"
            value={selectedApparatusId}
            onChange={(event) =>
              updateViewState({
                apparatusId: event.target.value,
                checkId: null,
                bagId: null,
                historyView: false
              })
            }
            disabled={busy}
          >
            {apparatus.map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
          </select>
        </label>
      </section>

      {!scopeReady ? (
        <section className="card stack-sm">
          <h3>Daily Truck Check</h3>
          <p className="muted">
            {busy ? "Loading daily truck check…" : "Daily truck check data is unavailable. Use Refresh to try again."}
          </p>
        </section>
      ) : activeCheck ? (
        <section className="card stack-sm">
          <div className="row-between">
            <h3 className="text-wrap-anywhere">
              {viewingHistoryCheckId ? "Opened Daily Check" : "Active Daily Check"}: {activeCheck.check.apparatusName}
            </h3>
            <div className="actions-wrap">
              <span className={`truck-check-status ${statusClass(activeCheck.check.status)}`}>
                {statusLabel(activeCheck.check.status)}
              </span>
              <span className="small muted">
                {activeCheck.progress.verifiedTasks}/{activeCheck.progress.totalTasks}
              </span>
              {activeCheck.check.status === "in_progress" ? (
                <button className="btn btn-secondary" onClick={() => setAbandonOpen(true)} disabled={busy}>
                  Quit Check
                </button>
              ) : null}
            </div>
          </div>
          <p className="muted small">
            Check by {activeCheck.check.startedByName} · {fmt(activeCheck.check.startedAt)}
          </p>

          {openNotice ? (
            <div className="truck-check-open-banner" role="status">
              {openNotice}
            </div>
          ) : null}

          {activeReadOnlyMessage ? (
            <div className="truck-check-readonly-banner" role="note">
              {activeReadOnlyMessage}
            </div>
          ) : null}

          <div className="bag-grid">
            {dailyBagsForDisplay.map((bag) => (
              <button
                key={bag.bagId}
                className="bag-tile btn-link"
                onClick={() => updateViewState({ bagId: bag.bagId })}
                style={{
                  borderColor: selectedBag?.bagId === bag.bagId ? "var(--red)" : undefined,
                  background: bag.complete ? "#f5fbf7" : "#fff"
                }}
              >
                <strong>{dailyBagDisplayName(bag.bagName)}</strong>
                <p className="small muted">
                  {bag.verifiedTasks}/{bag.totalTasks} verified
                </p>
              </button>
            ))}
          </div>

          {selectedBag ? (
            <div className="card-soft stack-sm">
              <h3>{dailyBagDisplayName(selectedBag.bagName)}</h3>
              <div className="stack-sm">
                {selectedBagTaskGroups.map((group) => (
                  <div key={group.name} className="kit-location-group stack-xs">
                    {shouldShowDailyGroupHeading(selectedBag.bagName, group.name) ? (
                      <h4 className="kit-location-heading">{group.name}</h4>
                    ) : null}
                    <div className="list">
                      {group.items.map((task) => {
                        const currentlyMissing = isDailyTaskMissing(task, selectedBag.tasks);
                        const verificationPrompt = task.taskType === "item" ? truckCheckVerificationPrompt(task.itemName) : null;
                        const problemNote = dailyProblemNote(task);
                        const verificationByline = dailyVerificationByline(task);
                        return (
                          <div key={task.taskId} className={`list-item static ${relatedRowClass(selectedBagRelatedMetaByKey[task.taskId])}`}>
                            <div className="stack-xs">
                              <div className="row gap-sm search-result-meta">
                                <strong className="text-wrap-anywhere">{dailyTaskDisplayName(task)}</strong>
                                {taskStatusChip(task)}
                                {currentlyMissing ? <span className="reason-chip reason-chip-yellow">Missing</span> : null}
                              </div>
                              {task.taskType === "item" ? (
                                <p className="muted small">
                                  Required {task.requiredQuantity ?? 0} · Qty {dailyDisplayQuantity(task)}
                                  {dailyDisplayExpiration(task) ? ` · Exp ${formatExpirationMonth(dailyDisplayExpiration(task))}` : ""}
                                </p>
                              ) : (
                                <DailyVisualHelp task={task} />
                              )}
                              {verificationPrompt ? (
                                <p className="muted small">
                                  <span className="reason-label">Verify:</span> {verificationPrompt}
                                </p>
                              ) : null}
                              {shouldShowDailyVisualSection(selectedBag.bagName, task) ? (
                                <p className="muted small">
                                  <span className="reason-label">Section:</span> {task.sectionName}
                                </p>
                              ) : null}
                              {problemNote ? (
                                <p className="small">
                                  <span className="reason-label">Problem note:</span> {problemNote}
                                </p>
                              ) : null}
                              {task.verified ? (
                                <p className={`small ${verificationByline.className}`}>
                                  {verificationByline.label} {task.verifiedByName ?? "—"} on {fmt(task.verifiedAt)}
                                </p>
                              ) : null}
                            </div>
                            {canModifyActiveCheck ? (
                              <button className="btn btn-secondary" onClick={() => openTaskModal(task)} disabled={busy}>
                                {task.verified ? "Update" : "Verify"}
                              </button>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {activeCheck.check.status === "in_progress" &&
          activeCheck.progress.totalTasks > 0 &&
          activeCheck.progress.verifiedTasks >= activeCheck.progress.totalTasks ? (
            <div className="row-end">
              <button className="btn btn-primary" onClick={() => setCompleteOpen(true)} disabled={busy}>
                Sign Off
              </button>
            </div>
          ) : null}
        </section>
      ) : (
        <section className="card stack-sm">
          <h3>New Daily Truck Check</h3>
          <label className="stack-xs">
            <span className="label">Your Name</span>
            <input className="input" value={startName} onChange={(event) => setStartName(event.target.value)} />
          </label>
          <label className="stack-xs">
            <span className="label">Note (optional)</span>
            <input className="input" value={startNote} onChange={(event) => setStartNote(event.target.value)} />
          </label>
          <div className="row-end">
            <button className="btn btn-primary" onClick={startCheck} disabled={busy || !selectedApparatusId || !startName.trim()}>
              {busy ? "Opening..." : "Open Daily Check"}
            </button>
          </div>
        </section>
      )}

      {scopeReady ? (
      <section className="card stack-sm">
        <div className="row-between">
          <h3>Daily Check History</h3>
          <select
            className="input inline-control"
            value={historyStatus}
            onChange={(event) => setHistoryStatus(event.target.value as typeof historyStatus)}
            disabled={historyBusy}
          >
            <option value="all">All</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="abandoned">Abandoned</option>
          </select>
        </div>
        <div className="list">
          {history.map((row) => (
            <div key={row.id} className="list-item static">
              <div className="stack-xs">
                <strong className="text-wrap-anywhere">{row.apparatusName}</strong>
                <div className="row gap-sm search-result-meta">
                  <span className={`truck-check-status ${statusClass(row.status)}`}>
                    {statusLabel(row.status)}
                  </span>
                  <span className="muted small">
                    {row.verifiedTasks}/{row.totalTasks} verified · {row.correctedTasks ?? 0} corrected · {row.deficientTasks ?? 0} deficient · {fmt(row.startedAt)}
                  </span>
                </div>
                <p className="muted small">Signed: {row.signoffDate ?? "—"}</p>
              </div>
              <div className="actions-wrap">
                <button className="btn btn-secondary" onClick={() => resumeHistoryCheck(row.id)} disabled={historyBusy}>
                  Open
                </button>
                <a className="btn btn-secondary" href={`/api/daily-truck-checks/${row.id}/csv`}>
                  CSV
                </a>
                {row.status === "abandoned" ? (
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setResumeCheck(row);
                      setResumeName(enteredName);
                      setResumeNote("");
                    }}
                    disabled={historyBusy}
                  >
                    Resume
                  </button>
                ) : null}
                {row.status === "abandoned" ? (
                  <button className="btn btn-danger" onClick={() => setDeleteCheck(row)} disabled={historyBusy}>
                    Delete
                  </button>
                ) : null}
              </div>
            </div>
          ))}
          {historyLoading ? <p className="small muted">Loading daily check history…</p> : null}
          {!historyLoading && history.length === 0 ? <p className="small muted">No daily truck check history yet.</p> : null}
        </div>
      </section>
      ) : null}

      {itemTaskModal && canModifyActiveCheck ? (
        <QuickEditSheet
          open
          resetKey={itemTaskModal.taskId}
          title={`Verify: ${itemTaskModal.itemName}`}
          initial={initialValueForTask(itemTaskModal, enteredName)}
          presentation="modal"
          sourceMode="apparatus"
          allowedSourceTypes={["stock", "other"]}
          requireRemoveReason
          removeReasonOptions={TRUCK_CHECK_REMOVE_REASON_OPTIONS}
          allowNoChangeSave
          disableSourceUntilChange
          showMove={false}
          showNotes
          stockLots={stockLots}
          stockLotsLoading={stockLotsLoading}
          verificationPrompt={truckCheckVerificationPrompt(itemTaskModal.itemName)}
          submitLabel="Save"
          onClose={closeTaskModal}
          onSave={(value) => verifyItemTask(value, "verify")}
          onAddExpiration={(value) => verifyItemTask(value, "add_expiration")}
          onRemove={itemTaskModal.inventoryRecordId ? (value) => verifyItemTask({ ...value, quantity: 0, expirationDate: "" }, "delete") : undefined}
        />
      ) : null}

      {visualTaskModal && canModifyActiveCheck ? (
        <div className="modal-backdrop" onClick={() => (busy ? undefined : closeTaskModal())}>
          <div className="modal-card move-sheet-card" onClick={(event) => event.stopPropagation()}>
            <ModalCloseButton onClick={closeTaskModal} disabled={busy} />
            <h3>Verify: {dailyTaskDisplayName(visualTaskModal)}</h3>
            {error ? <p className="error-text">{error}</p> : null}
            {shouldShowDailyVisualSection(selectedBag?.bagName ?? "", visualTaskModal) ? (
              <p className="muted small">{visualTaskModal.sectionName}</p>
            ) : null}
            <form onSubmit={submitVisualTask} className="stack-sm">
              <label className="stack-xs">
                <span className="label">Your Name</span>
                <input className="input" value={visualName} onChange={(event) => setVisualName(event.target.value)} required />
              </label>

              <label className="stack-xs">
                <span className="label">Status</span>
                <select className="input" value={visualStatus} onChange={(event) => setVisualStatus(event.target.value as typeof visualStatus)}>
                  <option value="verified">Verified</option>
                  <option value="problem">Report Problem</option>
                </select>
              </label>

              <label className="stack-xs">
                <span className="label">Note{visualStatus === "problem" ? " *" : " (optional)"}</span>
                <textarea className="input" rows={2} value={visualNote} onChange={(event) => setVisualNote(event.target.value)} required={visualStatus === "problem"} />
              </label>

              <div className="actions-wrap">
                <button type="button" className="btn btn-secondary" onClick={closeTaskModal} disabled={busy}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={busy || !visualName.trim() || (visualStatus === "problem" && !visualNote.trim())}>
                  {busy ? "Saving..." : "Save Verification"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {completeOpen ? (
        <div className="modal-backdrop" onClick={() => (busy ? undefined : setCompleteOpen(false))}>
          <div className="modal-card move-sheet-card" onClick={(event) => event.stopPropagation()}>
            <ModalCloseButton onClick={() => setCompleteOpen(false)} disabled={busy} />
            <h3>Sign Off Daily Check</h3>
            <form onSubmit={completeCheck} className="stack-sm">
              <label className="stack-xs">
                <span className="label">Your Name</span>
                <input className="input" value={completeName} onChange={(event) => setCompleteName(event.target.value)} required />
              </label>
              <DateInput label="Sign-off Date" value={completeDate} onChange={setCompleteDate} required allowEmpty={false} />
              <label className="stack-xs">
                <span className="label">Note (optional)</span>
                <input className="input" value={completeNote} onChange={(event) => setCompleteNote(event.target.value)} />
              </label>
              <div className="actions-wrap">
                <button type="button" className="btn btn-secondary" onClick={() => setCompleteOpen(false)} disabled={busy}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={busy || !completeName.trim()}>
                  {busy ? "Signing..." : "Sign Off"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {abandonOpen ? (
        <div className="modal-backdrop" onClick={() => (busy ? undefined : setAbandonOpen(false))}>
          <div className="modal-card move-sheet-card" onClick={(event) => event.stopPropagation()}>
            <ModalCloseButton onClick={() => setAbandonOpen(false)} disabled={busy} />
            <h3>Quit Daily Check?</h3>
            <p className="muted small">Inventory changes already made during this check will remain.</p>
            <form onSubmit={abandonCheck} className="stack-sm">
              <label className="stack-xs">
                <span className="label">Your Name</span>
                <input className="input" value={abandonName} onChange={(event) => setAbandonName(event.target.value)} required />
              </label>
              <label className="stack-xs">
                <span className="label">Note (optional)</span>
                <input className="input" value={abandonNote} onChange={(event) => setAbandonNote(event.target.value)} />
              </label>
              <div className="actions-wrap">
                <button type="button" className="btn btn-secondary" onClick={() => setAbandonOpen(false)} disabled={busy}>
                  Keep Checking
                </button>
                <button type="submit" className="btn btn-danger" disabled={busy || !abandonName.trim()}>
                  {busy ? "Quitting..." : "Quit Check"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {resumeCheck ? (
        <div className="modal-backdrop" onClick={() => (busy ? undefined : setResumeCheck(null))}>
          <div className="modal-card move-sheet-card" onClick={(event) => event.stopPropagation()}>
            <ModalCloseButton onClick={() => setResumeCheck(null)} disabled={busy} />
            <h3>Resume Abandoned Daily Check?</h3>
            <p className="muted small">This reloads the daily checklist from current apparatus inventory. Existing inventory will not be reset.</p>
            <form onSubmit={reopenAbandonedCheck} className="stack-sm">
              <div className="card-soft stack-xs">
                <strong>{resumeCheck.apparatusName}</strong>
                <p className="muted small">
                  {statusLabel(resumeCheck.status)} · {fmt(resumeCheck.startedAt)}
                </p>
              </div>
              <label className="stack-xs">
                <span className="label">Your Name</span>
                <input className="input" value={resumeName} onChange={(event) => setResumeName(event.target.value)} required />
              </label>
              <label className="stack-xs">
                <span className="label">Note (optional)</span>
                <input className="input" value={resumeNote} onChange={(event) => setResumeNote(event.target.value)} />
              </label>
              <div className="actions-wrap">
                <button type="button" className="btn btn-secondary" onClick={() => setResumeCheck(null)} disabled={busy}>
                  Keep Abandoned
                </button>
                <button type="submit" className="btn btn-primary" disabled={busy || !resumeName.trim()}>
                  {busy ? "Resuming..." : "Resume Check"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {deleteCheck ? (
        <div className="modal-backdrop" onClick={() => (busy ? undefined : setDeleteCheck(null))}>
          <div className="modal-card move-sheet-card" onClick={(event) => event.stopPropagation()}>
            <ModalCloseButton onClick={() => setDeleteCheck(null)} disabled={busy} />
            <h3>Delete Abandoned Daily Check?</h3>
            <p className="muted small">This removes the abandoned daily check report from history. Inventory changes already made during the check will remain.</p>
            <div className="card-soft stack-xs">
              <strong>{deleteCheck.apparatusName}</strong>
              <p className="muted small">
                {statusLabel(deleteCheck.status)} · {fmt(deleteCheck.startedAt)}
              </p>
            </div>
            <div className="actions-wrap">
              <button type="button" className="btn btn-secondary" onClick={() => setDeleteCheck(null)} disabled={busy}>
                Keep Check
              </button>
              <button type="button" className="btn btn-danger" onClick={deleteAbandonedCheck} disabled={busy}>
                {busy ? "Deleting..." : "Delete Check"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
